import '../index.css';

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../../shared/AuthContext';
import rolePermissions from '../../../shared/rolePermissions';

import http from '../../../api/http';



const ReportRenderer = ({ result }) => (
  <div className="page-stack">
    {result.components.map((component) => {
      const dataset = result.datasets[component.dataset];
      const rows = dataset?.rows || [];

      if (component.type === 'heading') {
        return <h2 className="section-title" key={component.handle}>{component.title}</h2>;
      }
      if (component.type === 'text') {
        return <p className="page-subtitle" key={component.handle}>{component.title}</p>;
      }
      if (component.type === 'metric') {
        const value = component.operation === 'sum' && component.field
          ? rows.reduce((sum, row) => sum + (Number(row[component.field]) || 0), 0)
          : dataset?.totalReturned || 0;
        return (
          <section className="card" key={component.handle}>
            <span className="kv-label">{component.title}</span>
            <strong className="section-title">{value}</strong>
            {dataset?.truncated && component.operation !== 'sum' ? <p className="page-subtitle">Based on the first {dataset.totalReturned} rows.</p> : null}
          </section>
        );
      }
      if (component.type === 'table') {
        return (
          <section className="card" key={component.handle}>
            <h3 className="section-title">{component.title}</h3>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>{(dataset?.columns || []).map((column) => <th key={column.key}>{column.label}</th>)}</tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={`${component.handle}-${index}`}>
                      {(dataset?.columns || []).map((column) => <td key={column.key}>{String(row[column.key] ?? '')}</td>)}
                    </tr>
                  ))}
                  {!rows.length ? (
                    <tr><td colSpan={dataset?.columns?.length || 1}>No rows returned.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {dataset?.truncated ? <p className="page-subtitle">Showing the first {dataset.totalReturned} rows; results were limited.</p> : null}
          </section>
        );
      }
      return null;
    })}
  </div>
);

const TEMPLATE_OPTIONS = [
  { value: 'custom', label: 'Custom template' },
  { value: 'executive-portfolio-summary', label: 'Executive portfolio summary' },
  { value: 'kpi-trend-report', label: 'KPI trend report' },
  { value: 'risk-watchlist-report', label: 'Risk & watchlist report' },
  { value: 'initiative-status-report', label: 'Initiative status report' },
  { value: 'activity-log-report', label: 'Activity log report' },
];

const nowIso = () => new Date().toISOString();

const toSlug = (value) => (
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
);

const normalizeRecord = (item = {}) => ({
  id: item.id || null,
  slug: toSlug(item.slug || item.name || item.id) || '',
  name: String(item.name || '').trim(),
  description: String(item.description || '').trim(),
  template: TEMPLATE_OPTIONS.some((option) => option.value === item.template)
    ? item.template
    : 'executive-portfolio-summary',
  windowDays: Number(item.windowDays) > 0 ? Number(item.windowDays) : 30,
  limit: Number(item.limit) > 0 ? Number(item.limit) : 20,
  status: String(item.status || 'draft'),
  schemaVersion: Number(item.schemaVersion) || 1,
  parameters: Array.isArray(item.parameters) ? item.parameters : [],
  datasets: Array.isArray(item.datasets) ? item.datasets : [],
  domain: Array.isArray(item.domain) ? item.domain : [],
  createdAt: item.createdAt || null,
  updatedAt: item.updatedAt || null,
});

const getReportFactory = () => {
  const Factory = require('../../../records/modules').default;
  return Factory({ slug: 'reports' }).Meta;
};

const listReportRecords = async () => {
  const response = await getReportFactory().getRecords({
    order: [['updatedAt', 'DESC']],
  });
  const rows = Array.isArray(response?.data) ? response.data : [];
  return rows.map(normalizeRecord);
};

const toTitleCase = (value) => (
  String(value || 'unknown')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const fetchSummary = async (windowDays) => {
  const response = await http.get('/reports/summary', { params: { windowDays } });
  return response.data;
};

const fetchActivity = async (limit) => {
  const response = await http.get('/reports/activity', { params: { limit } });
  return response.data;
};

const moduleKeyFromLabel = (moduleLabel) => {
  if (moduleLabel === 'Directional Strategies') return 'directionalStrategies';
  if (moduleLabel === 'StratPlans') return 'stratPlans';
  return 'sprintPacks';
};

const rowsFromSummary = (summary) => {
  const modules = summary?.modules || {};
  return [
    { module: 'Directional Strategies', ...(modules.directionalStrategies || {}) },
    { module: 'StratPlans', ...(modules.stratPlans || {}) },
    { module: 'SprintPacks', ...(modules.sprintPacks || {}) },
  ];
};

const runReportRecord = async (recordInput) => {
  const record = normalizeRecord(recordInput);

  if (record.template === 'kpi-trend-report') {
    const [summary7, summary30, summary90] = await Promise.all([
      fetchSummary(7),
      fetchSummary(30),
      fetchSummary(90),
    ]);
    return {
      title: record.name,
      description: record.description,
      generatedAt: summary30.generatedAt,
      columns: [
        { key: 'metric', label: 'Metric' },
        { key: 'window7', label: '7-day' },
        { key: 'window30', label: '30-day' },
        { key: 'window90', label: '90-day' },
      ],
      rows: [
        {
          metric: 'Upcoming commitments',
          window7: (summary7?.dateWindows?.upcoming?.directionalStrategies || 0) + (summary7?.dateWindows?.upcoming?.stratPlans || 0) + (summary7?.dateWindows?.upcoming?.sprintPacks || 0),
          window30: (summary30?.dateWindows?.upcoming?.directionalStrategies || 0) + (summary30?.dateWindows?.upcoming?.stratPlans || 0) + (summary30?.dateWindows?.upcoming?.sprintPacks || 0),
          window90: (summary90?.dateWindows?.upcoming?.directionalStrategies || 0) + (summary90?.dateWindows?.upcoming?.stratPlans || 0) + (summary90?.dateWindows?.upcoming?.sprintPacks || 0),
        },
        {
          metric: 'Overdue commitments',
          window7: (summary7?.dateWindows?.overdue?.directionalStrategies || 0) + (summary7?.dateWindows?.overdue?.stratPlans || 0) + (summary7?.dateWindows?.overdue?.sprintPacks || 0),
          window30: (summary30?.dateWindows?.overdue?.directionalStrategies || 0) + (summary30?.dateWindows?.overdue?.stratPlans || 0) + (summary30?.dateWindows?.overdue?.sprintPacks || 0),
          window90: (summary90?.dateWindows?.overdue?.directionalStrategies || 0) + (summary90?.dateWindows?.overdue?.stratPlans || 0) + (summary90?.dateWindows?.overdue?.sprintPacks || 0),
        },
      ],
    };
  }

  if (record.template === 'activity-log-report') {
    const activity = await fetchActivity(record.limit);
    const rows = Array.isArray(activity?.data) ? activity.data : [];
    return {
      title: record.name,
      description: record.description,
      generatedAt: nowIso(),
      columns: [
        { key: 'entityType', label: 'Entity type' },
        { key: 'label', label: 'Label' },
        { key: 'status', label: 'Status' },
        { key: 'updatedAt', label: 'Updated at' },
      ],
      rows: rows.map((item) => ({
        entityType: toTitleCase(item.entityType),
        label: item.label || item.entityId,
        status: toTitleCase(item.status),
        updatedAt: item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '-',
      })),
    };
  }

  const summary = await fetchSummary(record.windowDays);

  if (record.template === 'initiative-status-report') {
    const rows = [];
    const moduleMap = {
      directionalStrategies: 'Directional Strategies',
      stratPlans: 'StratPlans',
      sprintPacks: 'SprintPacks',
    };
    Object.entries(moduleMap).forEach(([moduleKey, label]) => {
      const statuses = summary?.modules?.[moduleKey]?.byStatus || {};
      Object.entries(statuses).forEach(([status, count]) => {
        rows.push({ module: label, status: toTitleCase(status), count });
      });
    });
    return {
      title: record.name,
      description: record.description,
      generatedAt: summary.generatedAt,
      columns: [
        { key: 'module', label: 'Module' },
        { key: 'status', label: 'Status' },
        { key: 'count', label: 'Count' },
      ],
      rows,
    };
  }

  const rows = rowsFromSummary(summary);

  if (record.template === 'risk-watchlist-report') {
    return {
      title: record.name,
      description: record.description,
      generatedAt: summary.generatedAt,
      columns: [
        { key: 'module', label: 'Module' },
        { key: 'overdue', label: 'Overdue' },
        { key: 'upcoming', label: `Upcoming (${record.windowDays}d)` },
        { key: 'watchScore', label: 'Watch score' },
      ],
      rows: rows.map((item) => {
        const moduleKey = moduleKeyFromLabel(item.module);
        const overdue = summary?.dateWindows?.overdue?.[moduleKey] || 0;
        const upcoming = summary?.dateWindows?.upcoming?.[moduleKey] || 0;
        return { module: item.module, overdue, upcoming, watchScore: overdue * 2 + upcoming };
      }).sort((a, b) => b.watchScore - a.watchScore),
    };
  }

  return {
    title: record.name,
    description: record.description,
    generatedAt: summary.generatedAt,
    columns: [
      { key: 'module', label: 'Module' },
      { key: 'total', label: 'Total' },
      { key: 'onTrack', label: 'On Track / Active' },
      { key: 'atRisk', label: 'At Risk / Off Track' },
      { key: 'overdue', label: 'Overdue' },
    ],
    rows: rows.map((item) => {
      const moduleKey = moduleKeyFromLabel(item.module);
      return {
        module: item.module,
        total: item.total || 0,
        onTrack: (item.byStatus?.on_track || 0) + (item.byStatus?.active || 0),
        atRisk: (item.byStatus?.at_risk || 0) + (item.byStatus?.off_track || 0),
        overdue: summary?.dateWindows?.overdue?.[moduleKey] || 0,
      };
    }),
  };
};

const escapeCsvValue = (value) => {
  const valueString = value == null ? '' : String(value);
  const raw = /^[\t\r ]*[=+\-@]/.test(valueString) ? `'${valueString}` : valueString;
  if (/[",\n\r]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`;
  return raw;
};

const escapeHtml = (value) => (
  String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
);

const exportCsv = (result) => {
  if (result.datasets) {
    const lines = [];
    Object.entries(result.datasets).forEach(([handle, dataset]) => {
      lines.push(escapeCsvValue(handle));
      lines.push(dataset.columns.map((column) => escapeCsvValue(column.label)).join(','));
      dataset.rows.forEach((row) => {
        lines.push(dataset.columns.map((column) => escapeCsvValue(row[column.key])).join(','));
      });
      lines.push('');
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${(result.title || 'report').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    return;
  }

  const headers = result.columns.map((column) => column.label);
  const lines = [headers.map(escapeCsvValue).join(',')];
  result.rows.forEach((row) => {
    lines.push(result.columns.map((column) => escapeCsvValue(row[column.key])).join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${(result.title || 'report').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const exportPdf = (result) => {
  const rowsHtml = result.datasets
    ? result.components.map((component) => {
      const dataset = result.datasets[component.dataset];
      if (component.type === 'heading') return `<h2>${escapeHtml(component.title)}</h2>`;
      if (component.type === 'text') return `<p>${escapeHtml(component.title)}</p>`;
      if (component.type === 'metric') {
        const value = component.operation === 'sum' && component.field
          ? dataset?.rows.reduce((sum, row) => sum + (Number(row[component.field]) || 0), 0)
          : dataset?.totalReturned || 0;
        return `<section><p>${escapeHtml(component.title)}</p><strong>${escapeHtml(value)}</strong></section>`;
      }
      if (component.type !== 'table' || !dataset) return '';
      const headers = dataset.columns.map((column) => `<th>${escapeHtml(column.label)}</th>`).join('');
      const rows = dataset.rows.map((row) => (
        `<tr>${dataset.columns.map((column) => `<td>${escapeHtml(row[column.key])}</td>`).join('')}</tr>`
      )).join('');
      return `<h2>${escapeHtml(component.title)}</h2><table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
    }).join('')
    : result.rows.map((row) => (
      `<tr>${result.columns.map((column) => `<td>${escapeHtml(row[column.key])}</td>`).join('')}</tr>`
    )).join('');

  const html = result.datasets ? `
    <html><head><title>${escapeHtml(result.title || 'Report')}</title><style>
      body { font-family: Arial, sans-serif; padding: 24px; color: #1f2937; }
      h1 { margin: 0 0 8px; font-size: 22px; }
      p { margin: 4px 0 14px; color: #4b5563; }
      table { width: 100%; border-collapse: collapse; margin-top: 16px; }
      th, td { border: 1px solid #d1d5db; padding: 8px; font-size: 12px; text-align: left; }
      th { background: #f3f4f6; }
    </style></head><body>
      <h1>${escapeHtml(result.title || 'Report')}</h1>
      <p>${escapeHtml(result.description || '')}</p>
      <p>Generated: ${escapeHtml(result.generatedAt || nowIso())}</p>
      ${rowsHtml}
    </body></html>
  ` : `
    <html>
      <head>
        <title>${escapeHtml(result.title || 'Report')}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #1f2937; }
          h1 { margin: 0 0 8px; font-size: 22px; }
          p { margin: 4px 0 14px; color: #4b5563; }
          table { width: 100%; border-collapse: collapse; margin-top: 16px; }
          th, td { border: 1px solid #d1d5db; padding: 8px; font-size: 12px; text-align: left; }
          th { background: #f3f4f6; }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(result.title || 'Report')}</h1>
        <p>${escapeHtml(result.description || '')}</p>
        <p>Generated: ${escapeHtml(result.generatedAt || nowIso())}</p>
        <table>
          <thead>
            <tr>${result.columns.map((column) => `<th>${escapeHtml(column.label)}</th>`).join('')}</tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body>
    </html>
  `;

  const printViaPopup = () => {
    const printWindow = window.open('', '_blank', 'noopener,noreferrer');
    if (!printWindow) return false;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    return true;
  };

  const printViaIframe = () => {
    const frame = document.createElement('iframe');
    frame.style.position = 'fixed';
    frame.style.right = '0';
    frame.style.bottom = '0';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    frame.setAttribute('aria-hidden', 'true');
    document.body.appendChild(frame);

    const doc = frame.contentWindow?.document;
    if (!doc || !frame.contentWindow) {
      document.body.removeChild(frame);
      throw new Error('Unable to initialize PDF export.');
    }

    doc.open();
    doc.write(html);
    doc.close();

    setTimeout(() => {
      frame.contentWindow.focus();
      frame.contentWindow.print();
      setTimeout(() => {
        if (document.body.contains(frame)) {
          document.body.removeChild(frame);
        }
      }, 1000);
    }, 50);
  };

  if (!printViaPopup()) {
    printViaIframe();
  }
};


const templateLabel = (value) => TEMPLATE_OPTIONS.find((item) => item.value === value)?.label || value;

const Component = () => {
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [records, setRecords] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [result, setResult] = useState(null);
  const [parameterValues, setParameterValues] = useState({});

  const { user, loadingSession } = useAuth();
  const canViewReports = rolePermissions.hasPermission(user, 'reports.view');
  const canManageContent = rolePermissions.hasPermission(user, 'content.manage');

  useEffect(() => {
    if (!canViewReports) return;

    const loadReports = async () => {
      setLoading(true);
      setError('');
      try {
        const rows = await listReportRecords();
        const visibleRows = canManageContent ? rows : rows.filter((record) => record.status === 'active');
        setRecords(visibleRows);
        setSelectedSlug((previous) => previous || visibleRows[0]?.slug || '');
      } catch (err) {
        setError(err?.response?.data?.message || err.message || 'Unable to load report definitions.');
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, [canViewReports, canManageContent]);

  const selectedRecord = useMemo(
    () => records.find((item) => item.slug === selectedSlug) || null,
    [records, selectedSlug]
  );

  useEffect(() => {
    if (!selectedRecord) {
      setParameterValues({});
      return;
    }
    setParameterValues(Object.fromEntries(
      selectedRecord.parameters.map((parameter) => [parameter.handle, parameter.defaultValue ?? ''])
    ));
  }, [selectedRecord]);

  if (loadingSession) return <p>Checking access...</p>;
  if (!canViewReports) return <p className="error-text">You do not have permission to view reports.</p>;
  if (loading) return <p>Loading report hub...</p>;

  return (
    <div className="page-stack">
      <section className="card">
        <div className="row-between">
          <div>
            <h1 className="section-title">Reporting Hub</h1>
            <p className="page-subtitle">Select a report definition, run it, and export results.</p>
          </div>
          <div className="action-cluster">
            <Link className="button-ghost" to="/reports">Manage Report Definitions</Link>
            {canManageContent ? <Link className="button-primary" to="/reports/new">Build a template</Link> : null}
          </div>
        </div>
      </section>

      {error ? <p className="error-text">{error}</p> : null}

      <section className="card">
        <div className="record-form-details-grid">
          <div className="record-form-details-item-wide">
            <span className="kv-label">Report Definition</span>
            <select
              value={selectedSlug}
              onChange={(event) => {
                setSelectedSlug(event.target.value);
                setResult(null);
              }}
            >
              {records.map((record) => (
                <option key={record.id} value={record.slug}>
                  {record.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {!records.length ? (
        <section className="card">
          <p className="page-subtitle">No report definitions found yet. Create one to start running reports.</p>
        </section>
      ) : null}

      {selectedRecord ? (
        <section className="card">
          <div className="row-between">
            <h2 className="section-title">{selectedRecord.name}</h2>
            <div className="action-cluster">
              <Link to={`/reports/${selectedRecord.slug}`}>Open Definition</Link>
              {canManageContent ? <Link to={`/reports/${encodeURIComponent(selectedRecord.slug)}/edit`}>Edit Template</Link> : null}
            </div>
          </div>
          <div className="stack-sm">
            <p className="page-subtitle">{selectedRecord.description || 'No description provided.'}</p>
            <p className="page-subtitle">Template: {templateLabel(selectedRecord.template)}</p>
            {selectedRecord.template !== 'custom' ? (
              <p className="page-subtitle">Window Days: {selectedRecord.windowDays} • Activity Limit: {selectedRecord.limit}</p>
            ) : null}
          </div>
          {selectedRecord.parameters.length ? (
            <div className="record-form-details-grid">
              {selectedRecord.parameters.map((parameter) => (
                <label key={parameter.handle}>
                  <span className="kv-label">{parameter.label || parameter.handle}</span>
                  {parameter.type === 'boolean' ? (
                    <input
                      type="checkbox"
                      checked={Boolean(parameterValues[parameter.handle])}
                      onChange={(event) => setParameterValues((previous) => ({ ...previous, [parameter.handle]: event.target.checked }))}
                    />
                  ) : parameter.type === 'select' ? (
                    <select
                      required={Boolean(parameter.required)}
                      value={parameterValues[parameter.handle] ?? ''}
                      onChange={(event) => setParameterValues((previous) => ({ ...previous, [parameter.handle]: event.target.value }))}
                    >
                      <option value="">Choose...</option>
                      {(parameter.options || []).map((option) => {
                        const value = typeof option === 'string' ? option : option.value;
                        const label = typeof option === 'string' ? option : option.label || option.value;
                        return <option key={value} value={value}>{label}</option>;
                      })}
                    </select>
                  ) : (
                    <input
                      type={parameter.type === 'number' ? 'number' : parameter.type === 'date' ? 'date' : 'text'}
                      required={Boolean(parameter.required)}
                      value={parameterValues[parameter.handle] ?? ''}
                      onChange={(event) => setParameterValues((previous) => ({
                        ...previous,
                        [parameter.handle]: parameter.type === 'number' && event.target.value !== '' ? Number(event.target.value) : event.target.value,
                      }))}
                    />
                  )}
                </label>
              ))}
            </div>
          ) : null}
          <div className="action-cluster" style={{ marginTop: 'var(--space-3)' }}>
            <button
              className="button-primary"
              type="button"
              disabled={running}
              onClick={async () => {
                setRunning(true);
                setError('');
                try {
                  const output = selectedRecord.template === 'custom'
                    ? (await http.post(`/reporting/${encodeURIComponent(selectedRecord.slug)}/run`, { parameters: parameterValues })).data
                    : await runReportRecord(selectedRecord);
                  setResult(output);
                } catch (err) {
                  setError(err?.response?.data?.message || err.message || 'Unable to run report.');
                } finally {
                  setRunning(false);
                }
              }}
            >
              {running ? 'Running...' : 'Run Report'}
            </button>
            <button type="button" onClick={() => result && exportCsv(result)} disabled={!result}>Export CSV</button>
            <button type="button" onClick={() => result && exportPdf(result)} disabled={!result}>Export PDF</button>
          </div>
        </section>
      ) : null}

      {result ? (
        <section className="card">
          <h3 className="section-title">Results</h3>
          <div className="report-result-meta">
            <strong>{result.title}</strong>
            <span>{result.description}</span>
            <span>Generated: {new Date(result.generatedAt || Date.now()).toLocaleString()}</span>
          </div>
          {result.datasets ? <ReportRenderer result={result} /> : <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  {result.columns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row, index) => (
                  <tr key={`result-row-${index}`}>
                    {result.columns.map((column) => (
                      <td key={`${column.key}-${index}`}>{row[column.key]}</td>
                    ))}
                  </tr>
                ))}
                {result.rows.length === 0 ? (
                  <tr>
                    <td colSpan={result.columns.length}>No rows returned.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>}
        </section>
      ) : null}
    </div>
  );
};

export default Component;